import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../../context/ToastContext';
import Breadcrumbs from '../../components/Breadcrumbs';
import Alert from '../../components/Alert';
import { SkeletonForm } from '../../components/SkeletonLoader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Field definitions with labels
const FORM_FIELDS = [
  { key: 'firstName', label: 'First Name', section: 'contact' },
  { key: 'lastName', label: 'Last Name', section: 'contact' },
  { key: 'email', label: 'Email', section: 'contact' },
  { key: 'phone', label: 'Phone', section: 'contact' },
  { key: 'propertyTitle', label: 'Property Title', section: 'auction' },
  { key: 'auctionUrl', label: 'eBay Auction URL', section: 'auction' },
  { key: 'ebayUsername', label: 'eBay Username', section: 'auction' },
  { key: 'winningBid', label: 'Winning Bid Amount', section: 'auction' },
  { key: 'preferredDueDay', label: 'Preferred Due Day', section: 'auction' },
  { key: 'mailingAddress', label: 'Mailing Address', section: 'address' },
  { key: 'mailingCity', label: 'City', section: 'address' },
  { key: 'mailingState', label: 'State', section: 'address' },
  { key: 'mailingZip', label: 'ZIP Code', section: 'address' },
  { key: 'notes', label: 'Additional Notes', section: 'other' }
];

function EbayFormSettings() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const toast = useToast();
  const demoDisabled = isDemoMode();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const [settings, setSettings] = useState({
    pageTitle: '',
    pageDescription: '',
    successTitle: '',
    successMessage: '',
    requiredFields: {},
    notificationEmail: ''
  });

  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/admin/ebay/form-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.settings) {
        setSettings(response.data.settings);
        setOriginalSettings(response.data.settings);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load form settings');
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(originalSettings));
  };

  const handleRequiredChange = (fieldKey, required) => {
    const newRequiredFields = { ...settings.requiredFields, [fieldKey]: required };
    const newSettings = { ...settings, requiredFields: newRequiredFields };
    setSettings(newSettings);
    setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(originalSettings));
  };

  const handleSave = async () => {
    if (demoDisabled) return;

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/admin/ebay/form-settings`,
        { settings },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setOriginalSettings(settings);
      setHasChanges(false);
      toast.success('Form settings saved successfully');
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setSettings(originalSettings);
      setHasChanges(false);
    }
    navigate('/admin/ebay-submissions');
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/admin' },
    { label: 'Tools', path: '/admin/tools-hub' },
    { label: 'eBay Submissions', path: '/admin/ebay-submissions' },
    { label: 'Form Settings' }
  ];

  if (loading) {
    return (
      <div className="admin-dashboard-container">
        <div className="admin-hub-header">
          <Breadcrumbs items={breadcrumbItems} />
          <button onClick={handleCancel} className="admin-hub-back-btn">
            <span className="admin-hub-back-arrow">←</span>
            Back to Submissions
          </button>
          <h1 className="admin-page-title">eBay Form Settings</h1>
        </div>
        <div className="card">
          <SkeletonForm fields={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <Breadcrumbs items={breadcrumbItems} />
        <button onClick={handleCancel} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Submissions
        </button>
        <h1 className="admin-page-title">eBay Winner Form Settings</h1>
        <p className="admin-hub-subtitle">Customize the auction winner registration form</p>
      </div>

      {/* Alert Messages */}
      {error && (
        <Alert type="error" message={error} onDismiss={() => setError('')} />
      )}

      {/* Page Content Settings */}
      <div className="card" style={{ padding: '25px', marginBottom: '25px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: 'var(--color-brand-text)' }}>
          Page Content
        </h2>

        <div className="form-group">
          <label className="form-label">Page Title</label>
          <input
            type="text"
            className="form-input"
            value={settings.pageTitle}
            onChange={(e) => handleChange('pageTitle', e.target.value)}
            placeholder="Congratulations on Your Winning Bid!"
          />
          <span className="form-hint">Displayed at the top of the form</span>
        </div>

        <div className="form-group">
          <label className="form-label">Page Description</label>
          <textarea
            className="form-input"
            value={settings.pageDescription}
            onChange={(e) => handleChange('pageDescription', e.target.value)}
            placeholder="Please complete this form so we can finalize your land purchase"
            rows={3}
            style={{ resize: 'vertical' }}
          />
          <span className="form-hint">Shown below the title to explain the form's purpose</span>
        </div>
      </div>

      {/* Success Message Settings */}
      <div className="card" style={{ padding: '25px', marginBottom: '25px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: 'var(--color-brand-text)' }}>
          Success Message
        </h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
          Displayed after the form is successfully submitted
        </p>

        <div className="form-group">
          <label className="form-label">Success Title</label>
          <input
            type="text"
            className="form-input"
            value={settings.successTitle}
            onChange={(e) => handleChange('successTitle', e.target.value)}
            placeholder="Thank You for Your Submission!"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Success Message</label>
          <textarea
            className="form-input"
            value={settings.successMessage}
            onChange={(e) => handleChange('successMessage', e.target.value)}
            placeholder="We've received your information and will contact you within 24 hours..."
            rows={4}
            style={{ resize: 'vertical' }}
          />
        </div>
      </div>

      {/* Required Fields Settings */}
      <div className="card" style={{ padding: '25px', marginBottom: '25px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: 'var(--color-brand-text)' }}>
          Required Fields
        </h2>
        <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
          Select which fields are required for form submission
        </p>

        {/* Contact Information */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ fontSize: '1rem', color: 'var(--color-text)', marginBottom: '15px', paddingBottom: '8px', borderBottom: '2px solid var(--color-border)' }}>
            Contact Information
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {FORM_FIELDS.filter(f => f.section === 'contact').map(field => (
              <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.requiredFields[field.key] ?? false}
                  onChange={(e) => handleRequiredChange(field.key, e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Auction Details */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ fontSize: '1rem', color: 'var(--color-text)', marginBottom: '15px', paddingBottom: '8px', borderBottom: '2px solid var(--color-border)' }}>
            Auction Details
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {FORM_FIELDS.filter(f => f.section === 'auction').map(field => (
              <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.requiredFields[field.key] ?? false}
                  onChange={(e) => handleRequiredChange(field.key, e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Mailing Address */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ fontSize: '1rem', color: 'var(--color-text)', marginBottom: '15px', paddingBottom: '8px', borderBottom: '2px solid var(--color-border)' }}>
            Mailing Address
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {FORM_FIELDS.filter(f => f.section === 'address').map(field => (
              <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.requiredFields[field.key] ?? false}
                  onChange={(e) => handleRequiredChange(field.key, e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>{field.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Other */}
        <div>
          <h3 style={{ fontSize: '1rem', color: 'var(--color-text)', marginBottom: '15px', paddingBottom: '8px', borderBottom: '2px solid var(--color-border)' }}>
            Other Fields
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
            {FORM_FIELDS.filter(f => f.section === 'other').map(field => (
              <label key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.requiredFields[field.key] ?? false}
                  onChange={(e) => handleRequiredChange(field.key, e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>{field.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="card" style={{ padding: '25px', marginBottom: '25px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: 'var(--color-brand-text)' }}>
          Notifications
        </h2>

        <div className="form-group">
          <label className="form-label">Notification Email</label>
          <input
            type="email"
            className="form-input"
            value={settings.notificationEmail}
            onChange={(e) => handleChange('notificationEmail', e.target.value)}
            placeholder="Leave empty to use your company email"
          />
          <span className="form-hint">
            Email address to receive new submission notifications. Leave empty to use your company's default email.
          </span>
        </div>
      </div>

      {/* Save Button */}
      <div className="card" style={{ padding: '20px', display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
        <button
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!hasChanges || saving || demoDisabled}
          style={{
            opacity: (!hasChanges || saving || demoDisabled) ? 0.5 : 1,
            cursor: (!hasChanges || saving || demoDisabled) ? 'not-allowed' : 'pointer'
          }}
          title={demoDisabled ? 'Disabled in demo mode' : ''}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

export default EbayFormSettings;
